import React, { useCallback, useMemo, useState } from 'react';
import { CandidateReferencerRelation, PublicReferencer, Query } from 'types';
import { useMutation, useQuery } from '@apollo/client';
import { Trans } from '@lingui/macro';
import { Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';

import SummaryItem from 'components/CandidatePublicProfileComponents/ReferenceSummaryCard/SummaryItem';
import Button from 'components/AntD/Button';
import Select from 'components/AntD/Select';
import MobileTextToggle from 'components/MobileTextToggle';
import { ReferencerItem } from 'components/CandidatePublicProfileComponents/ProfileCard';
import { FETCH_SHAREABLE_CRRS_FOR_ME } from 'api/candidate_shares_api';
import { trackEvent } from 'analytics';
import { UPDATE_REFERENCE_IS_PUBLIC } from 'api/candidate_api';
import { getNetworkError } from 'util/utilFunctions';
import { returnValidReferenceRequestsForRelations } from 'components/SharedFunctions';
import { FETCH_CANDIDATE_PUBLIC_PROFILE } from 'api/public_profile_api';
import classNames from 'classnames';
import { PUBLIC_PROFILE_SETTINGS_REFERENCES_UPDATED_EVENT } from 'components/CandidateComponents/PublicProfileSettings';

interface ReferenceSummariesProps {
  title: React.ReactNode;
  referencerDetails: PublicReferencer[];
  isPublic?: boolean;
}

const MAX_TO_SHOW = 3;

const ReferenceSummaries = ({
  referencerDetails,
  title = "Here's what past colleagues and managers have to say.",
  isPublic = false,
}: ReferenceSummariesProps) => {
  const [showAllSummaries, setShowAllSummaries] = useState(false);
  const { data: shareableCRRsForMe } = useQuery<Pick<Query, 'fetchShareableCRRsForMe'>>(
    FETCH_SHAREABLE_CRRS_FOR_ME
  );
  const [updateReferenceIsPublic, { loading: loadingReferences }] = useMutation(
    UPDATE_REFERENCE_IS_PUBLIC
  );
  const onChange = useCallback(
    async (_selected, lastSelected) => {
      try {
        await updateReferenceIsPublic({
          variables: {
            input: {
              candidateReferencerRelationId: +lastSelected.id,
              isPublic: !lastSelected.isPublic,
            },
          },
          refetchQueries: [FETCH_SHAREABLE_CRRS_FOR_ME, FETCH_CANDIDATE_PUBLIC_PROFILE],
          awaitRefetchQueries: true,
        });
        trackEvent(PUBLIC_PROFILE_SETTINGS_REFERENCES_UPDATED_EVENT);
      } catch (error) {
        console.error(error);
        alert(`Error updating status: ${getNetworkError(error)}`);
      }
    },
    [updateReferenceIsPublic]
  );

  const references: CandidateReferencerRelation[] = useMemo(
    () =>
      returnValidReferenceRequestsForRelations(shareableCRRsForMe?.fetchShareableCRRsForMe || []),
    [shareableCRRsForMe]
  );

  const contents = useMemo(
    () =>
      references.map(relation => ({
        id: relation.candidateReferencerRelationId,
        label: `${relation.referencerFirstName} ${relation.referencerLastName}`,
        isPublic: relation.isPublic,
      })),
    [references]
  );
  const numSelected = useMemo(
    () => references.reduce((acc, i) => acc + (i.isPublic ? 1 : 0), 0),
    [references]
  );

  return (
    <div className="tw-flex tw-flex-col">
      <h5
        className={classNames('tw-text-lg tw-font-semibold', {
          'tw-mb-2': !isPublic,
          'tw-mb-10': isPublic,
        })}
      >
        {title}
      </h5>
      {!isPublic && (
        <Select<ReferencerItem>
          id="toggle-references"
          mode="multiple"
          placement="bottomRight"
          className="tw-w-fit tw-mb-6"
          label={
            <MobileTextToggle
              text={
                <Trans>
                  Showing {numSelected}/{contents.length} References
                </Trans>
              }
              mobileText={<i className="fas fa-filter tw-text-gray-600" />}
            />
          }
          contents={contents}
          selected={contents.filter(({ isPublic }) => isPublic)}
          onChange={onChange}
          disableSearch
          showCaret
        />
      )}
      <div className="tw-flex tw-flex-col tw-gap-6">
        {loadingReferences ? (
          <Spin indicator={<LoadingOutlined spin />} />
        ) : referencerDetails.length > 0 ? (
          <>
            {referencerDetails
              .slice(0, showAllSummaries ? referencerDetails.length : MAX_TO_SHOW)
              .map(referencer => (
                <SummaryItem key={referencer.candidateReferencerRelationId} {...referencer} />
              ))}

            {referencerDetails.length > MAX_TO_SHOW && (
              <Button
                className="tw-font-medium tw-my-auto"
                variant="text"
                color="primary"
                onClick={() => setShowAllSummaries(!showAllSummaries)}
                size="small"
              >
                {showAllSummaries ? '- Collapse' : '+ See All'}
              </Button>
            )}
          </>
        ) : (
          <span className="tw-text-gray">Still waiting on references to share more.</span>
        )}
      </div>
    </div>
  );
};

export default ReferenceSummaries;
